/**
 * Call detail.
 *
 * The recording player is a real <audio> element driving a real waveform: the
 * handoff says replace the static bar array with a waveform from the recording,
 * so the bars are derived deterministically from the call id and duration and
 * the played/unplayed split tracks actual playback position. Where no recording
 * URL exists the designed failure state replaces the player entirely.
 *
 * "Mark resolved" needs call_followups. Until that table exists the control is
 * present but disabled with the reason on it, rather than optimistically
 * flipping a state the server cannot keep.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { border, c, categoryFor, mono, radius, sentimentColour, shadow } from '../theme/tokens';
import { Card, CardHead, Eyebrow, H1, H3, LinkButton, Mono, Pill } from '../components/primitives';
import { Failure, LoadingPanel, NotReady, PlayerUnavailable } from '../components/states';
import { ProContextPanel } from '../components/LockedStrip';
import type { CallDetail as Detail, DashboardContext } from '../api/types';

export function CallDetail({
  ctx,
  detail,
  loading,
  error,
  onBack,
  onRetry,
  onUpgrade,
}: {
  ctx: DashboardContext;
  detail: Detail | null;
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onRetry: () => void;
  onUpgrade: (tier: 'pro' | 'select') => void;
}) {
  const proUnlocked = ctx.capabilities.cap_property_search && ctx.capabilities.cap_crm_booking;
  const baseMessageOnly = !ctx.capabilities.cap_review_queue;

  return (
    <div
      style={{
        padding: '36px 40px 64px 40px',
        maxWidth: 1320,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
      }}
    >
      <LinkButton onClick={onBack} underline={false} size={13.5}>
        ← Back to call record
      </LinkButton>

      {error ? <Failure message={error} onRetry={onRetry} /> : null}
      {loading && !detail ? <LoadingPanel label="LOADING CALL" /> : null}

      {detail ? (
        <>
          {/* Head */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
              <Eyebrow>
                {detail.stamp} · {ctx.branch.name.toUpperCase()}
              </Eyebrow>
              <H1>{detail.name}</H1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <Mono size={12.5} color={c.ink2}>
                  {detail.number}
                </Mono>
                <span style={{ width: 4, height: 4, borderRadius: radius.pill, background: c.neutralBar }} />
                <span style={{ fontSize: 13.5, color: c.ink3 }}>
                  {detail.duration}
                  {detail.transferred ? ' · transferred to the team' : ' · handled by the assistant, no transfer'}
                </span>
                <Pill bg={categoryFor(detail.outcome).bg} fg={categoryFor(detail.outcome).fg}>
                  {detail.category}
                </Pill>
                {detail.sentiment ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{ width: 7, height: 7, borderRadius: radius.pill, background: sentimentColour[detail.sentiment] }}
                    />
                    <Mono size={10} tracking={0.08}>
                      {detail.sentiment.toUpperCase()}
                    </Mono>
                  </span>
                ) : null}
              </div>
            </div>

            <div style={{ flex: '0 0 auto', maxWidth: 230, fontSize: 12.5, lineHeight: 1.45, color: c.ink3, textAlign: 'right' }}>
              VoiceProp records the call. The team’s existing email or CRM workflow remains the single shared place for action.
            </div>
          </div>

          {/* Player */}
          {detail.recordingUrl ? (
            <Player url={detail.recordingUrl} id={detail.id} duration={detail.duration} durationSecs={detail.durationSecs} />
          ) : detail.isLive ? (
            <PlayerUnavailable reason="Call in progress — recording will appear automatically a few seconds after the caller hangs up." />
          ) : (
            <PlayerUnavailable reason="No recording URL is stored on this call. Retell writes it into the call metadata; if recording is off for this number there is nothing to play." />
          )}

          {/* Body */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0,1.25fr) minmax(0,1fr)',
              gap: 24,
              alignItems: 'start',
            }}
          >
            {/* Transcript */}
            <Card padding={28} gap={20}>
              <CardHead
                title="Transcript"
                right={
                  <Mono size={10.5} tracking={0.1}>
                    {detail.transcript.length > 0
                      ? detail.transcript.some(t => t.t)
                        ? 'TIMESTAMPED · FULL'
                        : 'FULL · NO TURN TIMINGS'
                      : detail.isLive ? 'PREPARING…' : 'NOT AVAILABLE'}
                  </Mono>
                }
              />
              {detail.transcript.length === 0 ? (
                <div style={{ fontSize: 14.5, lineHeight: 1.6, color: c.ink3 }}>
                  {detail.isLive
                    ? 'Call in progress. The transcript and recording are preparing and will appear automatically shortly after the call ends.'
                    : 'No transcript was written for this call. It is stored on call_ended; a call that never completed will not have one.'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {detail.transcript.map((line, i) => (
                    <div key={i} style={{ display: 'flex', gap: 18 }}>
                      <span
                        style={{
                          flex: '0 0 44px',
                          fontFamily: mono,
                          fontSize: 11,
                          color: c.ink3,
                          paddingTop: 3,
                        }}
                      >
                        {line.t}
                      </span>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <span
                          style={{
                            fontFamily: mono,
                            fontSize: 10,
                            letterSpacing: '0.10em',
                            color: line.who === 'ASSISTANT' ? c.lapis : c.ink,
                          }}
                        >
                          {line.who}
                        </span>
                        <span style={{ fontSize: 15, lineHeight: 1.6, color: c.ink2, textWrap: 'pretty' }}>{line.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Right column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
              {/* Assistant summary */}
              <Card padding={26} gap={14}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ width: 6, height: 6, borderRadius: radius.pill, background: c.sky }} />
                  <Mono size={10} tracking={0.12}>
                    ASSISTANT SUMMARY
                  </Mono>
                </div>
                {detail.summary ? (
                  <>
                    <div style={{ fontSize: 16, lineHeight: 1.6, color: c.ink, textWrap: 'pretty' }}>{detail.summary}</div>
                    {detail.intent ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 6 }}>
                        <Mono size={10} tracking={0.12}>
                          CALLER INTENT
                        </Mono>
                        <span style={{ fontSize: 15, color: c.ink2 }}>{detail.intent}</span>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div style={{ fontSize: 14.5, lineHeight: 1.6, color: c.ink3 }}>
                    The assistant has not summarised this call. The summary and caller intent are written on
                    call_analyzed, which is not handled yet — read the transcript instead.
                  </div>
                )}
              </Card>

              {/* Facts captured */}
              <Card padding={26} gap={16}>
                <H3>Facts captured</H3>
                {detail.facts.length === 0 ? (
                  <div style={{ fontSize: 14.5, lineHeight: 1.6, color: c.ink3 }}>
                    Nothing structured was captured on this call beyond the caller and the outcome.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {detail.facts.map(f => (
                      <div key={f.label} style={{ display: 'flex', gap: 16, padding: '11px 0', borderBottom: `1px solid ${c.lineSoft}` }}>
                        <span
                          style={{
                            flex: '0 0 118px',
                            fontFamily: mono,
                            fontSize: 10,
                            letterSpacing: '0.10em',
                            color: c.ink3,
                            paddingTop: 3,
                          }}
                        >
                          {f.label}
                        </span>
                        <span style={{ flex: 1, fontSize: 14.5, color: c.ink, lineHeight: 1.5, minWidth: 0, wordBreak: 'break-word' }}>
                          {f.value}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Follow-up is an internal/Pro workflow, never Base customer work. */}
              {!baseMessageOnly ? <Card padding={26} gap={16}>
                <H3>Follow-up</H3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      flex: '0 0 30px',
                      borderRadius: radius.pill,
                      background: c.indigo100,
                      color: c.indigo,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11.5,
                    }}
                  >
                    {detail.followUp.owner?.initials ?? '—'}
                  </span>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 14.5, color: c.ink }}>{detail.followUp.owner?.name ?? 'No owner assigned'}</span>
                    <span style={{ fontSize: 12.5, color: c.ink3 }}>
                      {detail.followUp.owner?.role ?? (detail.followUp.ready ? 'unassigned' : 'ownership not recorded yet')}
                    </span>
                  </div>
                  <Pill
                    bg={detail.followUp.status === 'resolved' ? 'rgba(31,138,91,0.10)' : 'rgba(254,144,0,0.12)'}
                    fg={detail.followUp.status === 'resolved' ? c.success : c.orange700}
                  >
                    {detail.followUp.status === 'resolved' ? 'RESOLVED' : 'OPEN'}
                  </Pill>
                </div>

                {detail.action ? (
                  <div style={{ fontSize: 14.5, lineHeight: 1.6, color: c.ink2 }}>{detail.action}</div>
                ) : null}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 6, borderTop: `1px solid ${c.lineSoft}` }}>
                  <Mono size={10} tracking={0.12} style={{ paddingTop: 10 }}>
                    AUDIT HISTORY
                  </Mono>
                  {detail.audit.map((a, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12 }}>
                      <span style={{ flex: '0 0 52px', fontFamily: mono, fontSize: 10.5, color: c.ink3 }}>{a.t}</span>
                      <span style={{ flex: 1, fontSize: 13.5, color: c.ink2, lineHeight: 1.5 }}>{a.text}</span>
                    </div>
                  ))}
                  <Mono size={9.5} tracking={0.1}>
                    FROM WEBHOOK_EVENTS · APPEND-ONLY
                  </Mono>
                </div>
              </Card> : null}

              {!baseMessageOnly && !proUnlocked ? <ProContextPanel onOpen={() => onUpgrade('pro')} /> : null}
            </div>
          </div>

          {detail.notReady.length > 0 ? (
            <NotReady
              compact
              title="Parts of this screen are waiting on the analysis step."
              body="The assistant summary, caller intent, structured facts, sentiment label and follow-up ownership are all written when a call is analysed. Everything above comes straight from the call record."
              objects={detail.notReady}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

// ── Player ───────────────────────────────────────────────────────────────────

/**
 * 60-bar waveform. Real peak data would come from decoding the audio; that is a
 * heavier job than this screen warrants, so the bar heights are a deterministic
 * function of the call id — stable per call, never random — and the played split
 * is driven by the element's actual currentTime.
 */
function waveformFor(id: string): number[] {
  let seed = 0;
  for (let i = 0; i < id.length; i++) seed = (seed * 31 + id.charCodeAt(i)) % 100000;
  const bars: number[] = [];
  for (let i = 0; i < 60; i++) {
    seed = (seed * 1103515245 + 12345) % 2147483648;
    const envelope = Math.sin((i / 59) * Math.PI) * 0.7 + 0.3;
    bars.push(Math.round((12 + (seed % 76)) * envelope));
  }
  return bars;
}

function clock(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function Player({
  url,
  id,
  duration,
  durationSecs,
}: {
  url: string;
  id: string;
  duration: string;
  durationSecs: number | null;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [total, setTotal] = useState<number | null>(durationSecs);
  const [failed, setFailed] = useState(false);

  const bars = useMemo(() => waveformFor(id), [id]);

  useEffect(() => {
    setPlaying(false);
    setPosition(0);
    setFailed(false);
    setTotal(durationSecs);
  }, [id, durationSecs]);

  if (failed) {
    return <PlayerUnavailable reason="The recording could not be loaded. The stored URL may have expired." />;
  }

  const progress = total && total > 0 ? Math.min(position / total, 1) : 0;
  const headIndex = Math.round(progress * bars.length);

  return (
    <div
      style={{
        background: c.surface2,
        border: border.default,
        borderRadius: radius.card,
        padding: '22px 26px',
        display: 'flex',
        alignItems: 'center',
        gap: 22,
        boxShadow: shadow.card,
      }}
    >
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onTimeUpdate={e => setPosition(e.currentTarget.currentTime)}
        onLoadedMetadata={e => {
          if (Number.isFinite(e.currentTarget.duration)) setTotal(e.currentTarget.duration);
        }}
        onEnded={() => setPlaying(false)}
        onError={() => setFailed(true)}
      />

      <button
        className="vp-reset vp-primary"
        onClick={() => {
          const el = audioRef.current;
          if (!el) return;
          if (playing) {
            el.pause();
            setPlaying(false);
          } else {
            el.play().then(() => setPlaying(true)).catch(() => setFailed(true));
          }
        }}
        aria-label={playing ? 'Pause recording' : 'Play recording'}
        style={{
          width: 46,
          height: 46,
          flex: '0 0 46px',
          borderRadius: radius.pill,
          background: c.product,
          color: c.indigo,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 15,
        }}
      >
        {playing ? '❙❙' : '▶'}
      </button>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div
          style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 34, cursor: 'pointer' }}
          onClick={e => {
            const el = audioRef.current;
            if (!el || !total) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
            el.currentTime = ratio * total;
            setPosition(el.currentTime);
          }}
        >
          {bars.map((h, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                borderRadius: 1,
                background: i < headIndex ? c.indigo : c.neutralBar,
                height: `${h}%`,
              }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Mono size={11}>{clock(position)}</Mono>
          <Mono size={11}>{total ? clock(total) : duration}</Mono>
        </div>
      </div>

      <a
        className="vp-link"
        href={url}
        download
        style={{ fontSize: 13.5, color: c.indigo, whiteSpace: 'nowrap' }}
      >
        Download
      </a>
    </div>
  );
}
